import React, { useEffect, useState } from "react";
import { X, Search } from "lucide-react";
import { api } from "../../lib/api";

/**
 * ShareNoteModal — selettore destinatari per una nota di riunione.
 *
 * Opzioni mutuamente esclusive sul radio, ma combinabili su checkbox:
 *   - Solo io (privata)
 *   - Staff specifici (multi-select operator attivi)
 *   - Tutti i partecipanti (share_mode="all_participants" — snapshot server-side)
 *   - Cliente della riunione (multi-select client_users del cliente)
 *
 * Props:
 *   - meeting: oggetto riunione (per `client_id` + eventual client users)
 *   - value: { share_mode, staff_user_ids, client_user_ids }
 *   - onChange(next): aggiornamento
 *   - onClose()
 */
export default function ShareNoteModal({ meeting, value, onChange, onClose }) {
    const [mode, setMode] = useState(value?.share_mode || "private");
    const [staffIds, setStaffIds] = useState(value?.staff_user_ids || []);
    const [clientIds, setClientIds] = useState(value?.client_user_ids || []);
    const [staffOptions, setStaffOptions] = useState([]);
    const [clientOptions, setClientOptions] = useState([]);
    const [staffQuery, setStaffQuery] = useState("");

    useEffect(() => {
        // Load active internal_users (staff pool)
        api.get("/internal/internal-users", { params: { active: true } })
            .then(({ data }) => setStaffOptions(data.items || data || []))
            .catch(() => setStaffOptions([]));
    }, []);

    useEffect(() => {
        if (!meeting?.client_id) { setClientOptions([]); return; }
        api.get(`/internal/clients/${meeting.client_id}`)
            .then(({ data }) => setClientOptions((data.client_users || []).filter(cu => cu.active !== false)))
            .catch(() => setClientOptions([]));
    }, [meeting?.client_id]);

    const toggleStaff = (id) => {
        setStaffIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const toggleClient = (id) => {
        setClientIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const apply = () => {
        let nextStaff = staffIds;
        let nextClients = clientIds;
        let nextMode = mode;
        if (mode === "private") { nextStaff = []; nextClients = []; }
        if (mode === "specific_staff") { nextClients = []; }
        if (mode === "all_participants") { nextStaff = []; /* snapshot server-side */ nextClients = []; }
        if (mode === "specific_clients") { nextStaff = []; }
        if (mode === "mixed") { /* keep both */ }
        onChange({
            share_mode: nextMode,
            staff_user_ids: nextStaff,
            client_user_ids: nextClients,
        });
        onClose();
    };

    const staffFiltered = staffOptions.filter(u => {
        if (!staffQuery.trim()) return true;
        const q = staffQuery.toLowerCase();
        return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });

    return (
        <div className="lt-modal-backdrop" onClick={onClose} data-testid="share-note-modal-backdrop">
            <div
                className="lt-modal"
                onClick={(e) => e.stopPropagation()}
                style={{ width: "min(520px, 94vw)", maxHeight: "86vh", display: "flex", flexDirection: "column" }}
                data-testid="share-note-modal"
            >
                <div className="lt-modal__head">
                    <h2 className="lt-modal__title">Condividi nota con…</h2>
                    <button className="lt-icon-btn" onClick={onClose} aria-label="Chiudi"><X size={14} /></button>
                </div>
                <div style={{ padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
                    <RadioOpt
                        testid="share-opt-private"
                        checked={mode === "private"}
                        onChange={() => setMode("private")}
                        label="Solo io (privata)"
                        hint="Nessun altro vedrà questa nota"
                    />
                    <RadioOpt
                        testid="share-opt-specific-staff"
                        checked={mode === "specific_staff"}
                        onChange={() => setMode("specific_staff")}
                        label="Staff specifici"
                        hint="Scegli uno o più colleghi"
                    />
                    {mode === "specific_staff" && (
                        <div style={{ paddingLeft: 24 }}>
                            <div style={{ position: "relative", marginBottom: 10 }}>
                                <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: "#8A8F88" }} />
                                <input
                                    type="text"
                                    placeholder="Cerca staff…"
                                    value={staffQuery}
                                    onChange={(e) => setStaffQuery(e.target.value)}
                                    data-testid="share-staff-search"
                                    style={{ width: "100%", padding: "8px 12px 8px 32px", border: "1px solid #DADEDA", borderRadius: 3, fontSize: 13 }}
                                />
                            </div>
                            <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #EEEFEA", borderRadius: 3 }}>
                                {staffFiltered.map((u) => (
                                    <label key={u.id || u._id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", cursor: "pointer", fontSize: 13 }}>
                                        <input
                                            type="checkbox"
                                            checked={staffIds.includes(u.id || u._id)}
                                            onChange={() => toggleStaff(u.id || u._id)}
                                            data-testid={`share-staff-opt-${u.id || u._id}`}
                                        />
                                        <span>{u.name} <span style={{ color: "#8A8F88", fontSize: 11 }}>{u.email}</span></span>
                                    </label>
                                ))}
                                {staffFiltered.length === 0 && (
                                    <div style={{ padding: 12, fontSize: 12, color: "#8A8F88" }}>Nessun risultato.</div>
                                )}
                            </div>
                        </div>
                    )}
                    <RadioOpt
                        testid="share-opt-all-participants"
                        checked={mode === "all_participants"}
                        onChange={() => setMode("all_participants")}
                        label="Tutti i partecipanti (staff)"
                        hint="Snapshot dei partecipanti interni al momento del salvataggio"
                    />
                    {meeting?.client_id && (
                        <>
                            <RadioOpt
                                testid="share-opt-specific-clients"
                                checked={mode === "specific_clients"}
                                onChange={() => setMode("specific_clients")}
                                label="Cliente della riunione"
                                hint="Il cliente vedrà la nota nel proprio portale"
                            />
                            {mode === "specific_clients" && (
                                <div style={{ paddingLeft: 24 }}>
                                    <div style={{ maxHeight: 140, overflowY: "auto", border: "1px solid #EEEFEA", borderRadius: 3 }}>
                                        {clientOptions.map((c) => (
                                            <label key={c.id || c._id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", cursor: "pointer", fontSize: 13 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={clientIds.includes(c.id || c._id)}
                                                    onChange={() => toggleClient(c.id || c._id)}
                                                    data-testid={`share-client-opt-${c.id || c._id}`}
                                                />
                                                <span>{c.name} <span style={{ color: "#8A8F88", fontSize: 11 }}>{c.email}</span></span>
                                            </label>
                                        ))}
                                        {clientOptions.length === 0 && (
                                            <div style={{ padding: 12, fontSize: 12, color: "#8A8F88" }}>Nessun utente cliente disponibile.</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="lt-form-actions" style={{ padding: "12px 20px", borderTop: "1px solid #EEEFEA" }}>
                    <div style={{ flex: 1 }} />
                    <button className="lt-btn-secondary" onClick={onClose}>Annulla</button>
                    <button className="lt-btn-primary" onClick={apply} data-testid="share-apply-btn">Applica</button>
                </div>
            </div>
        </div>
    );
}

function RadioOpt({ checked, onChange, label, hint, testid }) {
    return (
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
            <input type="radio" checked={checked} onChange={onChange} style={{ marginTop: 3 }} data-testid={testid} />
            <div>
                <div style={{ fontSize: 14, color: "#181B20", fontWeight: 500 }}>{label}</div>
                {hint && <div style={{ fontSize: 12, color: "#8A8F88", marginTop: 2 }}>{hint}</div>}
            </div>
        </label>
    );
}
